/**
 * Live Trading MTM Summary & Hero View Model Calculations.
 *
 * Aggregates realized/unrealized P&L across live positions and builds MTM hero bar descriptors.
 */

export interface LiveMtmSummary {
  realized: number;
  unrealized: number;
  mtm: number;
  openCount: number;
}

export interface MtmHeroViewModel {
  realized: number;
  realizedText: string;
  realizedClass: string;
  unrealized: number;
  unrealizedText: string;
  unrealizedClass: string;
  mtm: number;
  mtmText: string;
  mtmClass: string;
  arrowText: string;
  countText: string;
}

/**
 * Computes aggregate realized, unrealized, total MTM, and open position counts.
 */
export function computeLiveMtmSummary(
  positionsList: any[] | Record<string, any>,
  unrealizedOf: (p: any) => number,
): LiveMtmSummary {
  const list = Array.isArray(positionsList)
    ? positionsList
    : positionsList && typeof positionsList === "object"
    ? Object.values(positionsList)
    : [];

  let realized = 0;
  let unrealized = 0;
  let openCount = 0;

  for (const p of list) {
    if (!p) continue;
    realized += p.realized || 0;
    const u = typeof unrealizedOf === "function" ? unrealizedOf(p) : 0;
    unrealized += u;
    if (p.netqty !== 0) {
      openCount++;
    }
  }

  const mtm = realized + unrealized;
  return { realized, unrealized, mtm, openCount };
}

/**
 * Formats MTM summary metrics into view model properties for DOM updates.
 */
export function buildMtmHeroViewModel(
  realized: number,
  unrealized: number,
  openCount: number,
  inrFormatter: (n: number) => string,
): MtmHeroViewModel {
  const mtm = realized + unrealized;
  const fmt =
    typeof inrFormatter === "function" ? inrFormatter : (n: number) => String(n);

  return {
    realized,
    realizedText: fmt(Math.abs(realized)),
    realizedClass: `mtm-stat-val ${realized >= 0 ? "pos" : "neg"}`,
    unrealized,
    unrealizedText: fmt(Math.abs(unrealized)),
    unrealizedClass: `mtm-stat-val ${unrealized >= 0 ? "pos" : "neg"}`,
    mtm,
    mtmText: fmt(Math.abs(mtm)),
    mtmClass: mtm >= 0 ? "positive" : "negative",
    arrowText: mtm >= 0 ? "▲" : "▼",
    countText: String(openCount),
  };
}
